import { EventBundleProductComponentDto } from 'api/dtos/events/products';
import { Event, EventPricingTier } from 'legacy/common/types/models';
import { Waiver } from 'legacy/components/pages/Events/EventForm/Groups/Waiver/EventWaiverModal';
import { GroupDto } from 'legacy/groups/types';
import { FriendStatus, LeanUserDto, UserLean } from 'legacy/user/types';
import { SurveyModel } from 'survey-core';
import { Item } from './components/offers/offer-create/OfferEligibility/components/ItemList';

type ContactHostBody = {
  name: string;
  email: string;
  message: string;
};

export interface ContactHostOptions {
  eventId: string;
  details: ContactHostBody;
}

export interface RequestApprovalParams {
  eventId: string;
  tierId: string;
  answers: { questionId: string; answer: string }[];
}

export interface EventBasic {
  _id: string;
  title: string;
  startDate: string;
  seriesId?: string;
  endDate: string;
  prePurchaseFormId?: string;
  photo: {
    url?: string;
    position?: 'center' | 'wide';
  };
}

export interface EventLean extends EventBasic {
  host: UserLean;
  totalGuests?: number;
}

export type EventGuest = UserLean & {
  id?: string;
  photoUrl?: string;
  friendStatus: FriendStatus;
};

/**
 * `event.tiers[]` element returned for public (users who are not event organizers)
 */
export type EventTierForPublic = Pick<
  EventPricingTier,
  | '_id'
  | 'name'
  | 'description'
  | 'price'
  | 'private'
  | 'status'
  | 'remaining'
  | 'isDisabled'
  | 'startDate'
  | 'endDate'
  | 'imageUrl'
  | 'requiredAssociationIds'
  | 'requiredOrganizationIds'
>;

/**
 * `event.tiers[]` element returned for organizers (host, cohost, operator)
 */
export type EventTierForOrganizer = EventTierForPublic &
  Pick<EventPricingTier, 'shareToken' | 'maxAttendance' | 'totalGuests'>;

/**
 * Populated event organizers
 * This includes `event.host`, `event.cohosts`, `event.operators`, `event.scanners`, and `event.photographers`.
 */
export type EventOrganizers = {
  host: UserLean;
  cohosts?: UserLean[];
  operators: UserLean[];
  scanners: UserLean[];
  photographers: UserLean[];
};

export enum MomentumType {
  SELLING_FAST_30_MINUTES = 'SELLING_FAST_30_MINUTES',
  SELLING_FAST_24_HOURS = 'SELLING_FAST_24_HOURS',
  VIEWING_NOW = 'VIEWING_NOW',
  VIEWING_24_HOURS = 'VIEWING_24_HOURS'
}

export type MomentumChip = {
  type: MomentumType;
  displayCount: number;
};

/**
 * Event returned for public (users who are not event organizers)
 */
export type EventForPublic = EventBasic &
  EventOrganizers &
  Pick<
    Event,
    | 'description'
    | 'address'
    | 'coordinates'
    | 'gallery'
    | 'totalGuests'
    | 'remainingGuests'
    | 'ticketNotesRequest'
    | 'googleForms'
    | 'endDate'
    | 'private'
    | 'status'
    | 'pricing'
    | 'showGuests'
    | 'maxAttendance'
    | 'userHostStatus'
    | 'userAttendanceStatus'
    | 'activeStatus'
    | 'isFree'
    | 'canEdit'
    | 'canDelete'
    | 'isOrganizer'
    | 'isScanner'
    | 'isPhotographer'
    | 'createdAt'
    | 'maxTicketsPerTransaction'
    | 'metaPixelId'
    | 'waiver'
    | 'isDraft'
    | 'updatedAt'
    | 'album'
    | 'requestToPublishFormId'
    | 'requestToPublishWorkflowId'
    | 'associationId'
    | 'enableEntryApprovalControls'
  > & {
    guestPreview?: EventGuest[];
    /**
     * @deprecated use `guestPreview` instead
     */
    friendsAttending?: EventGuest[];
    /** only returned on nearby events queries */
    distance?: number;
    groups?: GroupDto[];
    waivers?: Waiver[];
    momentumChip?: MomentumChip;
  };

/**
 * Event returned for operators
 */
export type EventForOperator = EventForPublic &
  Pick<
    Event,
    | 'shareToken'
    | 'requestListSummary'
    | 'shouldRequestToPublish'
    | 'requestToPublishFormId'
    | 'requestToPublishWorkflowId'
    | 'associationId'
  >;

/**
 * Event returned for hosts or cohosts
 */
export type EventForHost = EventForOperator &
  Pick<Event, 'payoutStatus' | 'lastVerificationNotif'> & {
    payoutDestination?: EventPayoutDestination;
    canRequestEarlyPayout?: boolean;
    availablePayoutDestinations: {
      hostBanks: PayoutBank[];
      associationsBanks: PayoutBank[];
    };
  };

export type EventForUser = EventForHost | EventForOperator | EventForPublic;

export type EventSummary = {
  totals: {
    payoutAmount: number;
    guests: number;
    ticketsSold: number;
    scannedGuests: number;
  };
  payoutCurrency: 'CAD' | 'USD';
};

export type EventAnalytics = {
  discountCodes: {
    activeCount: number;
    pausedCount: number;
    revenue: number;
  };
  tickets: {
    addon: { sales: number; scans: number };
    admission: { sales: number; scans: number };
    revenue: number;
    sales: number;
    views: number;
  };
  trackingLinks: {
    count: number;
    revenue: number;
    sales: number;
    views: number;
  };
};

export type EventTierCreation = {
  id?: string;
  name: EventPricingTier['name'];
  description?: EventPricingTier['description'];
  price: EventPricingTier['price'];
  private?: EventPricingTier['private'];
  maxAttendance?: EventPricingTier['maxAttendance'] | string;
  numberToDuplicate?: number;
  isDisabled?: boolean;
  requiresApproval?: boolean;
  questions?: { question: string; _id: string }[];
  isPrivate?: boolean;
  shareToken?: string;
  isVisible?: boolean;
};

export interface VerifyTicketResultDto {
  answers: { questionId: string; question: string; answer: string }[];
  tierRequestAnswers: TierApprovalAnswer[];
  transaction?: {
    amount: number;
    currency: 'CAD' | 'USD';
    user: {
      id: string;
      firstName: string;
      lastName: string;
      name: string;
      photoUrl?: string;
    };
    tier: {
      id: string;
      name: string;
      imageUrl?: string;
    };
  };
  name?: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    name: string;
    photoUrl?: string;
  };
  tier?: {
    id: string;
    name: string;
    imageUrl?: string;
  };
  hasAlreadyScanned: boolean;
  scanDate: string;
  ticketId?: string;
}

export interface LeanEventDto {
  id: string;
  title: string;
  address: string;
  photoUrl?: string;
  startDate: Date;
  endDate?: Date;
}

export interface DiscountCodeType {
  type: string;
}

export interface RequiredEventRating {
  event: LeanEventDto;
}

export interface RequiredEventRatingsDto {
  requiredRatings: RequiredEventRating[];
}

export enum Impression {
  GOOD = 'good',
  OK = 'ok',
  BAD = 'bad'
}

export interface SubmitEventRatingBody {
  overall?: {
    score?: number;
  };
  safety?: {
    explanation?: string;
    impression?: Impression;
  };
}

export interface SuggestedFriendsFromEventDto {
  eventId: string;
  suggestedFriends: SuggestedFriendDto[];
}

export interface SuggestedFriendDto {
  score: number;
  user: LeanUserDto & { friendStatus: FriendStatus };
}

export interface TicketInfoRequiredResponse {
  googleForms: {
    required: boolean;
    url?: string;
  };
  ticketNotes: {
    required: boolean;
    guestQuestions?: { id: string; question: string }[];
  };
}

export interface AnalyticsDto {
  views: number;
  sources: { source: string; views: number }[];
  trackingLinks: {
    id: string;
    name: string;
    pathname: string;
    views: number;
    sales: number;
    revenue: number;
    sources: {
      source: string;
      views: number;
      sales: number;
      revenue: number;
    }[];
  }[];
}

export enum CodeType {
  DiscountCode = 'discountCode',
  PromoterCode = 'promoterCode'
}

export interface DiscountCodeDto {
  id: string;
  eventId: string;
  code: string;
  discount: {
    kind: 'amount' | 'percentage';
    value: number;
  };
  numberOfUses: number;
  isActive: boolean;
  maxUsage?: number;
  startDate?: Date;
  endDate?: Date;
  createdAt: string;
  removedAt?: string;
  codeType: CodeType;
  tierIds?: string[];
}

export interface DiscountCodeAnalyticsDto {
  discountCodeId: string;
  revenue: number;
}

export interface EventDiscountCodesAnalyticsDto {
  activeCount: number;
  pausedCount: number;
  totalRevenue: number;
}

export interface EventDiscountCodesDto {
  eventId: string;
  analytics: EventDiscountCodesAnalyticsDto;
  discountCodes: Array<DiscountCodeDto>;
}

export interface EventWaiverDto {
  isWaiverSet: boolean;
  waiverContent?: string;
  waivers?: Waiver[];
}
export type TierScreeningQuestion = {
  id?: string;
  question: string;
};

export enum TierTypes {
  Admission = 'admission',
  Addon = 'addon'
}

export type TierDto = {
  id: string;
  type: TierTypes;
  name: string;
  description?: string;
  price: number;
  isPrivate: boolean;
  shareToken?: string;
  isDisabled: boolean;
  requiresApproval: boolean;
  totalGuests: number;
  maxAttendance?: number;
  questions?: TierScreeningQuestion[];
  requiredOrganizationIds: string[];
  requiredAssociationIds: string[];
  imageUrl?: string;
  startDate?: string;
  endDate?: string;
  canDelete: boolean;
  sortIndex?: number;
  sectionId?: string;
  isVisible?: boolean;

  // bundle fields -- this is used so we don't have to shake every single place that uses the tier dto
  components?: EventBundleProductComponentDto[];
};

export type GetEventTiersDto = {
  items: TierDto[];
  sections: SectionDto[];
};

export type RequestToPublishFormDto = {
  id: string;
  schema: SurveyModel;
};

export type SubmitRequestToPublishFormDto = {
  formId: string;
  id: string;
  response: {
    [key: string]: unknown;
  };
  userId: string;
};

export type CreateTierScreeningQuestion = {
  question: string;
};

export type UpdateTierScreeningQuestion = {
  id?: string;
  question: string;
};

export type UpdateTierDto = Pick<
  TierDto,
  | 'type'
  | 'name'
  | 'description'
  | 'price'
  | 'isPrivate'
  | 'maxAttendance'
  | 'isDisabled'
  | 'requiresApproval'
  | 'questions'
  | 'imageUrl'
  | 'startDate'
  | 'endDate'
  | 'requiredOrganizationIds'
  | 'requiredAssociationIds'
> & {
  questions?: UpdateTierScreeningQuestion[];
};

export type CreateTierDto = Pick<
  TierDto,
  | 'type'
  | 'name'
  | 'description'
  | 'price'
  | 'isPrivate'
  | 'maxAttendance'
  | 'isDisabled'
  | 'requiresApproval'
  | 'imageUrl'
  | 'shareToken'
  | 'startDate'
  | 'endDate'
> & {
  questions?: CreateTierScreeningQuestion[];
};

export type CreateWaiverDto = {
  title: string;
  content: string;
};

export type UpdateWaiverDto = {
  title: string;
  content: string;
};

export type WaiverDto = {
  id: string;
  title: string;
  content: string;
};

export type CreateDiscountCodeBody = Pick<
  DiscountCodeDto,
  'code' | 'discount' | 'maxUsage' | 'startDate' | 'endDate' | 'tierIds'
>;

export interface CreateDiscountCodeParams {
  eventId: string;
  body: CreateDiscountCodeBody;
}

export type UpdateDiscountCodeBody = Pick<
  DiscountCodeDto,
  | 'code'
  | 'discount'
  | 'maxUsage'
  | 'startDate'
  | 'endDate'
  | 'isActive'
  | 'tierIds'
>;

export interface UpdateDiscountCodeParams {
  eventId: string;
  discountCodeId: string;
  body: UpdateDiscountCodeBody;
}

export interface DeleteDiscountCodeParams {
  eventId: string;
  discountCodeId: string;
}

export interface TextAllGuestsDto {
  message: string;
}

export interface TextAllGuestsParams {
  tierId?: string;
  eventId: string;
  message: string;
}

export interface PutPayoutDestinationParams {
  bankId?: string;
  eventId: string;
}

export type EventPayoutDestination = {
  stripeId: string;
  last4: string;
  country: 'CA' | 'US';
  type: 'bank' | 'card';
  institution: string;
  name?: string;
  holderName?: string;
  updatedAt?: Date;
  createdAt?: Date;
};

export interface PostEarlyPayoutParams {
  eventId: string;
}

export enum EarlyPayoutRequestResult {
  SUCCESS = 'SUCCESS',
  NO_ORGANIZATION = 'NO_ORGANIZATION',
  NOT_HOST = 'NOT_HOST',
  ORG_USER_NOT_MEMBER = 'ORG_USER_NOT_MEMBER',
  ORG_EARLY_PAYOUTS_UNSET = 'ORG_EARLY_PAYOUTS_UNSET',
  ORG_EARLY_PAYOUTS_REVIEWING = 'ORG_EARLY_PAYOUTS_REVIEWING',
  ORG_EARLY_PAYOUTS_DENIED = 'ORG_EARLY_PAYOUTS_DENIED',
  EVENT_ENDED = 'EVENT_ENDED',
  EVENT_WITH_PROMOTERS = 'EVENT_WITH_PROMOTERS'
}

export type PayoutBank = {
  id: string;
  stripeId: string;
  bankName: string;
  last4: string;
  label?: string;
};

export interface UploadPhotoResponseDto {
  photo: any;
}

export interface UploadVideoResponseDto {
  hypeVideoUrl: string;
}

export interface CreateUploadVideoUrlResponseDto {
  uploadUrl: string;
  videoId: string;
}

type EventDiscountCode = {
  eventId: string;
  code: string;
  isActive: boolean;
  maxUsage?: number;
  discount: {
    kind: 'amount' | 'percentage';
    value: number;
  };
  startDate?: string;
  endDate?: string;
  id?: string;
};

export type EventCreation = {
  title?: string;
  address?: string;
  description?: string;
  groups?: string[];
  cohosts?: string[];
  operators?: string[];
  scanners?: string[];
  photographers?: string[];
  showGuests?: boolean;
  photo?: {
    url?: string;
    position?: 'center' | 'wide';
  };
  /** website formprops sometimes send as a string, so we accept both (mongoose handles casting) */
  private?: boolean;
  video?: {
    enabled?: boolean;
    type?: 'group' | 'webinar' | 'custom';
    customUrl?: string;
  };
  startDate?: string;
  endDate?: string;
  googleForms?: {
    url?: string;
    embedUrl?: string;
  };
  ticketNotesRequest?: string;
  coordinates?: number[];
  pricing?: {
    tiers: EventPricingTier[];
    currency: 'CAD' | 'USD';
    transactionFeesPaidBy?: 'host' | 'guest';
    tips: {
      areEnabled: boolean;
      label?: string;
    };
    tax?: {
      taxId: string;
      name: string;
      ratePercent: number;
    };
  };
  maxTicketsPerTransaction?: number;
  discountCodes?: EventDiscountCode[];
  waiver?: {
    content: string;
  };
  waivers?: Waiver[];
  chargeOrganizationFee?: boolean;
  momentsPermissions?: {
    createMoments: 'admin' | 'member';
    commentMoments: 'admin' | 'member';
  };
  metaPixelId?: string;
};

type EventTaxCreation = {
  taxId?: string;
  name?: string;
  ratePercent?: number;
};

type EventTipsCreation = {
  areEnabled?: boolean;
  label?: string;
};

type EventPricingCreation = {
  tips?: EventTipsCreation;
  transactionFeesPaidBy?: 'host' | 'guest';
  tiers: EventTierCreation[];
  tax?: EventTaxCreation;
  currency?: 'CAD' | 'USD';
};

type EventPricingEdit = Partial<
  // TODO: Also omit 'transactionFeesPaidBy' once the frontend stops sending it.
  Omit<EventPricingCreation, 'tiers'> & {
    /** NOTE: we allow `any` here since the frontend sends both existing and new tiers when editing events.
     * We should probably fix this in the frontend so that it only sends relevant properties and we can use stronger typing here.
     */
    tiers: (EventTierCreation | any)[];
  }
>;

export type EventEdit = Partial<
  Pick<
    EventCreation,
    | 'title'
    | 'description'
    | 'startDate'
    | 'endDate'
    | 'address'
    | 'coordinates'
    | 'private'
    | 'cohosts'
    | 'operators'
    | 'scanners'
    | 'photographers'
    | 'groups'
    | 'showGuests'
    | 'ticketNotesRequest'
    | 'googleForms'
    | 'video'
    | 'maxTicketsPerTransaction'
    | 'metaPixelId'
    | 'discountCodes'
    | 'waiver'
    | 'chargeOrganizationFee'
    | 'momentsPermissions'
    | 'photo'
    | 'waivers'
  > & {
    pricing: EventPricingEdit;
  } & {
    isDraft: boolean;
  }
>;

export type HostedEventsDto = {
  items: HostedEventDto[];
  nextOffset?: number;
};

export type HostedEventDto = {
  id: string;
  title?: string;
  media: MediaDto[];
  startDate?: Date;
  endDate?: Date;
  host?: HostedEventUserDto;
  cohosts: HostedEventUserDto[];
  groups: HostedEventGroupDto[];
  isDraft: boolean;
  guestsCount?: number;
  metadata: HostedEventMetadataDto;
  createdAt: Date;
};

type HostedEventMetadataDto = {
  commentsCount: number;
  reactionsCount: number;
  currentUser: HostedEventCurrentUserInfo;
};

type HostedEventCurrentUserInfo = {
  isOrganizer: boolean;
  reaction?: HostedEventReactionDto;
  ticketsCount: number;
};

type HostedEventUserDto = {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
};

type HostedEventGroupDto = {
  id: string;
  name: string;
  badgeImageUrl?: string;
};

type HostedEventReactionDto = {
  id: string;
  type: string;
};

export interface TierApprovalAnswer {
  questionId: string;
  question?: string;
  answer: string;
}

export interface TierApprovalTier {
  id: string;
  name: string;
}

export interface TierApprovalUser {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl: string;
  email: string;
}

export interface TierApproval {
  id: string;
  tier: TierApprovalTier;
  status: 'approved' | 'rejected' | 'pending';
  readStatus: 'read' | 'unread';
  answers: TierApprovalAnswer[];
  user: TierApprovalUser;
  createdAt: Date;
  updatedAt: Date;
}

export interface TierApprovals {
  pages: any;
  items: TierApproval[];
}

export interface GuestEntryUser {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
  email?: string;
}

export interface GuestEntryTier {
  id: string;
  name: string;
}

export interface EntryLogItem {
  type: 'scanned' | 'admitted';
  date: Date;
  user?: GuestEntryUser;
}

export interface GuestEntryListItem {
  id: string;
  type: 'ticket' | 'complimentary';
  name?: string;
  user?: GuestEntryUser;
  tier?: GuestEntryTier;

  latestEntry?: EntryLogItem;

  isScanned: boolean;

  scanDate: Date;
}

export interface EventQuestion {
  questionId: string;
  question: string;
  answer: string;
}

export interface GuestEntryDto {
  id: string;
  type: 'ticket' | 'complimentary';
  name?: string;
  user?: GuestEntryUser;
  purchaser?: GuestEntryUser;
  purchasedAt?: Date;
  tier?: GuestEntryTier;
  createdBy?: GuestEntryUser;
  createdAt?: Date;
  entryLog: EntryLogItem[];
  screeningQuestions?: EventQuestion[];
  bundleProductId?: string;
  bundleProductName?: string;
  bundleInstanceId?: string;
  guestQuestions?: EventQuestion[];
}

export interface GuestEntryList {
  pages: any;
  items: GuestEntryListItem[];
}

export interface MomentDto {
  id: string;
  media: MediaDto[];
  metadata: {
    numberOfComments: number;
    numberOfReactions: number;
    recentReactions: ReactionDto[];
  };
  permissions: {
    canComment: boolean;
    canEdit: boolean;
  };
  caption: string;
  creator: MomentCreatorDto;
  createdAt: Date;
  updatedAt: Date;
}

export interface MediaDto {
  url?: string;
  downloadUrl?: string;
  videoId?: string;
  thumbnail?: string;
  type: string;
}

export interface MomentCreatorDto {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl: string;
}

export type MomentUploadType = 'professional' | 'consumer';

export interface CreateMomentMetadata {
  uploadType: MomentUploadType;
}

export interface CreateMoment {
  media: CreateMomentMedia[];
  caption?: string;
  metadata?: CreateMomentMetadata;
}

export interface CreateMomentMedia {
  url: string;
  type: 'image' | 'video';
}

export interface ReactionDto {
  id: string;
  type:
    | 'heart'
    | 'fire'
    | 'money_mouth_face'
    | 'call_me_hand'
    | 'rolling_on_the_floor_laughing'
    | 'party_face';
  creator: ReactionCreator;
}

export interface ReactionCreator {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
  friendStatus?: FriendStatus;
}

export enum RecurringFrequency {
  Weekly = 'weekly',
  BiWeekly = 'biweekly',
  Monthly = 'monthly'
}

export const RECURRING_FREQUENCY_LABELS = {
  [RecurringFrequency.Weekly]: 'Weekly',
  [RecurringFrequency.BiWeekly]: 'Bi-Weekly',
  [RecurringFrequency.Monthly]: 'Monthly'
};

export interface RecurringEventDto {
  canEdit: boolean;
  seriesId: string;
  startDate: string;
  endDate: string;
  frequency: RecurringFrequency;
  eventIds: string[];
}

export interface EventAnalyticsSummaryDto {
  totalRevenue: number;
  totalRSVP: number;
  totalTicketsSold: number;
  totalTicketsScanned: number;
  scannedRate: number;
  avgSafetyRating?: number;
  safetyRatingSubmissionCount?: number;
}

export interface EventAnalyticsRevenueSummaryDto {
  grossRevenue: number;
  netRevenue: number;
  grossSales: number;
  netSales: number;
  taxes: number;
  organizerFees: number;
  marketingFees: number;
}

interface EventAnalyticsTicketRevenueOverTimeDataPointDto {
  date: Date;
  revenue: number;
}

export interface EventAnalyticsTicketRevenueOverTimeDto {
  data: EventAnalyticsTicketRevenueOverTimeDataPointDto[];
}

export interface EventAnalyticsTicketScansByTierDataPointDto {
  tierId: string;
  tierName: string;
  ticketsScanned: number;
  ticketsSold: number;
}

export interface EventAnalyticsTicketScansByTierDto {
  data: EventAnalyticsTicketScansByTierDataPointDto[];
}

interface EventAnalyticsDiscountCodeUsageDataPointDto {
  discountCodeId: string;
  discountCode: string;
  uses: number;
  maxUsage?: number;
}

export interface EventAnalyticsDiscountCodeUsageDto {
  data: EventAnalyticsDiscountCodeUsageDataPointDto[];
}

interface EventAnalyticsDailyRevenueDataPointDto {
  date: Date;
  revenue: number;
}

export interface EventAnalyticsDailyRevenueDto {
  data: EventAnalyticsDailyRevenueDataPointDto[];
  totalRevenue: number;
}

export interface EventAnalyticsGuestsDataPointDto {
  id: string;
  guestName: string;
  bundleProductId?: string;
  bundleProductName?: string;
  bundleInstanceId?: string;
  ticketTierName: string;
  purchaseDate: Date;
  totalScans: number;
  lastScanned: Date;
  discountCode: string;
  trackingLink: string;
}

export interface EventAnalyticsGuestsDto {
  items: EventAnalyticsGuestsDataPointDto[];
  previousCursor?: string;
  nextCursor?: string;
  totalResults: number;
  pages: number;
  currentPage: number;
}

export type EventAnalyticsGuestsSortBy =
  | 'purchaseDate'
  | 'guestName'
  | 'lastScanned';

interface EventAnalyticsRevenueAllTimeDataPointDto {
  date: Date;
  revenue: number;
}

export interface EventAnalyticsRevenueAllTimeDto {
  data: EventAnalyticsRevenueAllTimeDataPointDto[];
  totalRevenue: number;
}

export type EventAnalyticsTicketTierSalesSortBy = 'grossRevenue' | 'tierName';

interface EventAnalyticsTicketTierSalesDataPointDto {
  id: string;
  ticketTierName: string;
  type: 'public' | 'private';
  price: number;
  ticketsSold: number;
  grossRevenue: number;
  netRevenue: number;
}

export interface EventAnalyticsTicketTierSalesDto {
  items: EventAnalyticsTicketTierSalesDataPointDto[];
  previousCursor?: string;
  nextCursor?: string;
  totalResults: number;
  pages: number;
  currentPage: number;
}

export type EventAnalyticsTicketTiersSortBy = 'ticketsScanned' | 'tierName';

export interface EventAnalyticsTicketTiersDataPointDto {
  id: string;
  name: string;
  isPrivate: boolean;
  type: 'admission' | 'addon';
  availableTickets?: number;
  ticketsPurchased: number;
  ticketsScanned: number;
  scannedRate: number;
}

export interface EventAnalyticsTicketTiersDto {
  items: EventAnalyticsTicketTiersDataPointDto[];
  previousCursor?: string;
  nextCursor?: string;
  totalResults: number;
  pages: number;
  currentPage: number;
}

export type EventAnalyticsTicketTiersSalesSortBy =
  | 'price'
  | 'ticketsSold'
  | 'grossRevenue'
  | 'netRevenue';

export enum OfferEligibilityType {
  USERS = 'users',
  ORGANIZATIONS = 'organizations',
  ASSOCIATIONS = 'associations'
}

export type PromoterDiscountType = {
  value: number;
  kind: 'amount' | 'percentage';
  usageLimitPerContract?: number;
  startDate?: Date;
  endDate?: Date;
  isActive?: boolean;
};

export type OfferEligibility = {
  id: string;
  mustHaveTicket: boolean;
  selector: OfferEligibilityType;
  items?: Item[];
};

export type OfferType = {
  name: string;
  eligibilityCriteria: OfferEligibility[];
  rewards: {
    kickbackRewards: {
      kind: 'amount' | 'percentage';
      value: number;
      saleConditions: {
        unit: 'ticket' | 'order';
        minSalesThreshold?: number;
        tiers: PromoterOfferContractTierDto[];
        minSalesThresholdEnabled?: boolean;
      };
    };
  };
  discount?: PromoterDiscountType;
  contracts?: PromoterOfferContractDto[];
};

export type OfferTypeWithIds = Omit<
  OfferType,
  'eligibilityCriteria' | 'rewards'
> & {
  rewards: {
    kickbackRewards: {
      kind: 'amount' | 'percentage';
      value: number;
      saleConditions: {
        unit: 'ticket' | 'order';
        tierIds: string[];
        minSalesThreshold?: number;
      };
    }[];
  };
  eligibilityCriteria: {
    mustHaveTicket: boolean;
    selector: 'users' | 'organizations' | 'associations';
    ids: string[];
  }[];
};

export interface CreateOfferParams {
  eventId: string;
  body: OfferTypeWithIds;
}

export interface PromoterOfferContractDto {
  id: string;
  promoter: {
    id: string;
    name: string;
    photoUrl?: string;
  };
  offer: {
    id: string;
    name: string;
    isActive: boolean;
  };
  event: {
    id: string;
    name: string;
    photoUrl: string;
  };
  attributionCode: string;
  attributionUrl: string;
  analytics: {
    views: number;
    sales: number;
    revenue: number;
    earnings: number;
  };
  isActive: boolean;
  selector?: 'users' | 'organizations' | 'associations';
  discount?: {
    kind: 'amount' | 'percentage';
    value: number;
    usageLimitPerContract?: number;
    numberOfDiscountCodeUses: number;
  };
}

export type PromoterOfferContractTierDto = {
  id: string;
  name: string;
  price: string;
};
export type PromoterOfferSelectorItemDto = {
  id: string;
  name: string;
  photoUrl?: string;
};

export interface PromoterOfferDto {
  id: string;
  name: string;
  eventId: string;
  eligibilityCriteria: {
    mustHaveTicket: boolean;
    selector: 'users' | 'organizations' | 'associations';
    ids: string[];
    users?: PromoterOfferSelectorItemDto[];
    organizations?: PromoterOfferSelectorItemDto[];
    associations?: PromoterOfferSelectorItemDto[];
  }[];
  isActive: boolean;
  rewards: {
    kickbackRewards: {
      kind: 'amount' | 'percentage';
      value: number;
      saleConditions: {
        unit: 'ticket' | 'order';
        tierIds: string[];
        minSalesThreshold?: number;
        tiers?: PromoterOfferContractTierDto[];
      };
    }[];
  };
  discount?: {
    kind: 'amount' | 'percentage';
    value: number;
    isActive: boolean;
    usageLimitPerContract?: number;
    startDate?: Date;
    endDate?: Date;
  };
  activePromoters: number;
  analytics: {
    views: number;
    sales: number;
    revenue: number;
    earnings: number;
  };
  contracts: PromoterOfferContractDto[];
}

export interface PromoterOffer {
  id: string;
  name: string;
  eventId: string;
  eligibilityCriteria: {
    mustHaveTicket: boolean;
    selector: 'users' | 'organizations' | 'associations';
    ids: string[];
  }[];
  isActive: boolean;
  rewards: {
    kickbackRewards: {
      kind: 'amount' | 'percentage';
      value: number;
      saleConditions: {
        unit: 'ticket' | 'order';
        tierIds: string[];
        minSalesThreshold?: number;
      };
    }[];
  };
  discount?: {
    kind: 'amount' | 'percentage';
    value: number;
    usageLimitPerContract?: number;
    startDate?: Date;
    endDate?: Date;
  };
}

export interface UpdateOfferStatusParams {
  offerId: string;
  isActive: boolean;
}

export interface DeletePromoterOfferParams {
  offerId: string;
}

export interface PausePromoterContractParams {
  contractId: string;
  isActive: boolean;
}

export interface TierReorderDto {
  tierId: string;
  sortIndex: number;
  sectionId?: string;
}

export interface SectionDto {
  id: string;
  name: string;
  sortIndex: number;
}

export interface SectionReorderDto {
  id: string;
  sortIndex: number;
}
